import { forwardRef, type KeyboardEvent } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

import {
  SLIDER_FILL_CLASSES,
  SLIDER_INPUT_CLASSES,
  SLIDER_ROOT_CLASSES,
  SLIDER_TRACK_CLASSES,
} from './Slider.tokens';
import { type SliderProps } from './Slider.types';

/** The keys the browser would move the thumb with; a read-only slider eats them. */
const VALUE_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
]);

/**
 * A value in a bounded range, felt rather than typed.
 *
 * On the native `<input type="range">` — see `Slider.tokens.ts` for why, and
 * note the part that matters most: the browser mirrors a range control in RTL by
 * itself, so the maximum sits at the inline end in Arabic without a single
 * direction branch. Every hand-built slider in every kit gets that wrong at
 * least once.
 *
 * `isReadOnly` is honoured by hand because `readOnly` is **ignored** on a range
 * input: the pointer and the arrow keys would both still move it, so the change
 * is dropped and the keys are eaten, and the caller announces the refusal.
 */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(function Slider(
  {
    id,
    className,
    value,
    onValueChange,
    min,
    max,
    step = 1,
    disabled = false,
    isReadOnly = false,
    onRefuse,
    'aria-label': ariaLabel,
    'aria-describedby': describedBy,
    'aria-valuetext': valueText,
    onBlur,
  },
  ref,
) {
  const current = value ?? min;
  const span = max - min;
  // Genuine runtime data, so an inline style rather than a class (U6's carve-out).
  const filled = span <= 0 ? 0 : ((current - min) / span) * 100;

  const refuse = (): boolean => {
    if (disabled) return true;
    if (!isReadOnly) return false;
    onRefuse?.();
    return true;
  };

  return (
    <span className={cn(SLIDER_ROOT_CLASSES, className)}>
      <span aria-hidden className={SLIDER_TRACK_CLASSES} />
      <span
        aria-hidden
        // `width`, not `inline-size`: the fill's SIZE is not directional — only
        // where it is anchored is, and `start-0` on the class already says that.
        // (`inline-size` is also unimplemented in jsdom, so a spec could not see
        // it.) The percentage is runtime data, hence an inline style (U6).
        data-slider-fill
        className={SLIDER_FILL_CLASSES}
        style={{ width: `${String(filled)}%` }}
      />
      <input
        ref={ref}
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={current}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-valuetext={valueText}
        aria-readonly={isReadOnly ? true : undefined}
        onBlur={onBlur}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (VALUE_KEYS.has(event.key) && refuse()) event.preventDefault();
        }}
        onChange={(event) => {
          if (refuse()) return;
          onValueChange(Number(event.target.value));
        }}
        className={SLIDER_INPUT_CLASSES}
      />
    </span>
  );
});
